import { useMemo } from 'react';
import type { ComponentType } from 'react';
import { toast, Toaster } from 'react-hot-toast';
import { useHistory } from 'react-router-dom';

import { copyTextToClipboard } from '@nw/shared/utils';

import * as Icons from '../../icons';

import '../ToastUtils/styles.css';

import {
  IconCard,
  IconGrid,
  IconGroupBadge,
  IconGroupHeader,
  IconGroupSection,
  IconGroupTitle,
  IconLabel,
  IconPreviewContainer,
  IconPreviewHeader,
  IconPreviewHomeButton,
  IconPreviewPage,
  IconPreviewStats,
  IconPreviewTitle,
  IconPreviewTopBar,
  IconSizeLabel,
  SimpleToastContainer,
  SimpleToastText
} from './styledComponents';

type IconComponent = ComponentType<Record<string, unknown>>;

interface IconDefinition {
  name: string;
  Component: IconComponent;
}

const getIconDimensions = (Component: IconComponent): string | null => {
  if (Component.defaultProps) {
    const width = Component.defaultProps['width'] || 'N/A';
    const height = Component.defaultProps['height'] || 'N/A';
    return `${width}×${height}`;
  }
  return null;
};

const iconEntries: IconDefinition[] = Object.entries(Icons)
  .filter(([, component]) => typeof component === 'function')
  .map(([name, Component]) => ({ name, Component: Component as IconComponent }))
  .sort((first, second) => first.name.localeCompare(second.name));

const IconsPreview = () => {
  const history = useHistory();
  const groupedIcons = useMemo(() => {
    const groups: Record<string, IconDefinition[]> = {};

    iconEntries.forEach((icon) => {
      const groupKey = icon.name.charAt(0).toUpperCase();
      if (!groups[groupKey]) {
        groups[groupKey] = [];
      }
      groups[groupKey].push(icon);
    });

    return Object.entries(groups)
      .sort((first, second) => first[0].localeCompare(second[0]))
      .map(([group, items]) => ({
        group,
        items: items.sort((firstIcon, secondIcon) =>
          firstIcon.name.localeCompare(secondIcon.name)
        )
      }));
  }, []);

  const renderedIconCount = useMemo(
    () => groupedIcons.reduce((count, group) => count + group.items.length, 0),
    [groupedIcons]
  );

  const handleNavigateHome = () => {
    history.push('/');
  };

  const handleCopyIconName = (iconName: string) => {
    copyTextToClipboard(iconName);
    toast.remove();
    toast.custom(
      (t) => {
        return (
          <div className={SimpleToastContainer}>
            <span className={SimpleToastText}>
              Copied icon name: '{iconName}'
            </span>
          </div>
        );
      },
      { duration: 500, position: 'bottom-center' }
    );
  };

  return (
    <div className={IconPreviewPage}>
      <Toaster
        position="top-center"
        reverseOrder={false}
        containerClassName="icons-preview-toast-container"
      />
      <div className={IconPreviewTopBar}>
        <button
          type="button"
          className={IconPreviewHomeButton}
          onClick={handleNavigateHome}
          aria-label="Back to home"
        >
          <span>Back to home</span>
        </button>
      </div>
      <header className={IconPreviewHeader}>
        <p className={IconPreviewStats}>
          <span>Shared resources</span>
          <span>•</span>
          <span>{renderedIconCount} icons</span>
        </p>
        <h1 className={IconPreviewTitle}>Icons Library Preview</h1>
      </header>

      <div className={IconPreviewContainer}>
        {groupedIcons.map(({ group, items }) => (
          <section key={group} className={IconGroupSection}>
            <div className={IconGroupHeader}>
              <h2 className={IconGroupTitle}>{group}</h2>
              <span className={IconGroupBadge}>{items.length} icons</span>
            </div>
            <div className={IconGrid}>
              {items.map(({ name, Component }) => {
                const dimensions = getIconDimensions(Component);
                return (
                  <div
                    key={name}
                    className={IconCard}
                    onClick={() => handleCopyIconName(name)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleCopyIconName(name);
                      }
                    }}
                    aria-label={`Copy icon name ${name}`}
                  >
                    <Component />
                    <p className={IconLabel}>{name}</p>
                    {dimensions && (
                      <p className={IconSizeLabel}>{dimensions}</p>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
};
export { IconsPreview };
